import axios, { AxiosError, AxiosResponse } from "axios";
import { unref } from "vue";
import { RefOrString, RefOrBase } from "@/types/BaseTypes";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { assignObject, getNotNullObject } from "@/helpers/units";
import { ResponseType } from "axios";

interface ISenderConfig {
  lang?: RefOrString;
  loc?: ISenderLoc;
  notification?: ISenderNotification;
}
interface ISenderLocStrong {
  serviceUnavailable: RefOrString;
  401: RefOrString;
  otherError: RefOrString;
  unknownError: RefOrString;
}
type ISenderLocKeys = keyof ISenderLocStrong;
type ISenderLoc = Partial<ISenderLocKeys>;

interface ISenderNotificationStrong {
  error: (s: string) => void;
  success: (s: string) => void;
  info: (s: string) => void;
  warning: (s: string) => void;
}
type ISenderNotification = Partial<ISenderNotificationStrong>;

type ISenderRequest = RefOrBase<"get" | "post" | "put" | "delete" | "update">;

export interface ISenderSendParams {
  endpoint: RefOrString;
  method: ISenderRequest;
  params?: object;
  data?: unknown;
  contentType?: string;
  responseType?: ResponseType | undefined;
  /**
   * Если произошла ошибка при отправке запроса, всё равно резолвить промис
   */
  resolveCrash?: boolean;
  headers?: { [key: string]: string };
  preventNotification?: boolean;
}

/**
 * Занимается отправкой запросов и выводом ошибок в запросах с поддержкой реактивности
 */
export class Sender {
  localStorageKey = "sender-axios";
  // lang: RefOrString;
  notification: ISenderNotificationStrong = {
    error: (s) => console.log(`Error: ${s}`),
    success: (s) => console.log(`Error: ${s}`),
    info: (s) => console.log(`Error: ${s}`),
    warning: (s) => console.log(`Error: ${s}`),
  };
  loc: ISenderLocStrong = {
    serviceUnavailable: "serviceUnavailable",
    401: "Auth error",
    otherError: "error",
    unknownError: "Unknown error",
  };

  /**
   *
   * @param {RefOrString} baseUrl
   * @param {ISenderConfig} config
   *
   */
  constructor(baseUrl: RefOrString, config: ISenderConfig = {}) {
    this.baseUrl = baseUrl;
    //const lsItem = localStorage.getItem(this.localStorageKey)
    //if (lsItem) this.authorization = lsItem;
    //if (config?.auth) this.setAuth(config.auth);

    // this.lang = config.lang ?? "ru";

    assignObject(config.loc, this.loc);

    // if (getNotNullObject(config.notification)) {
    //   assignObject(getNotNullObject(config.notification), this.notification);
    // }
  }

  _baseUrl: RefOrString = "";

  get baseUrl() {
    return unref(this._baseUrl);
  }

  set baseUrl(v: RefOrString) {
    this._baseUrl = v;
  }

  notifyError(errorText = "") {
    this.notification.error(errorText);
  }

  notifySuccess(successText = "") {
    this.notification.success(successText);
  }

  notifyInfo(infoText = "") {
    this.notification.info(infoText);
  }

  notifyWarning(warningText = "") {
    this.notification.warning(warningText);
  }

  error(err: AxiosError, text = null) {
    console.warn(`-----REQUEST REJECTED-----\n`, err);

    const t = (k: ISenderLocKeys) => this._getloc(k);

    let errText;
    switch (err.code) {
      case "ERR_NETWORK":
        errText = t("serviceUnavailable");
        break;
      case "ERR_BAD_REQUEST":
        if (err.response && err.response.status) {
          const status: ISenderLocKeys =
            `${err.response.status}` as ISenderLocKeys;
          errText = t(status);
        } else {
          errText = `${t("otherError")}`;
        }
        break;
      default:
        errText = t("unknownError");
    }
    this.notifyError(`${text ?? errText}`);
  }

  _getloc(key: ISenderLocKeys): string {
    if (key in this.loc) return unref(this.loc[key]);
    return unref(this.loc[key]);
  }

  /**
   * send request to endpoint
   * @param sendParams {ISenderSendParams}
   * @returns {Promise<any>}
   */
  send({
    endpoint,
    method = "get",
    params,
    data,
    contentType,
    responseType,
    resolveCrash = false,
    headers = {},
    preventNotification = false,
  }: ISenderSendParams): Promise<AxiosResponse> {
    const endpointStr = unref(endpoint);
    const methodStr = unref(method);

    return new Promise((resolve, reject) => {
      if (typeof params !== "object") params = {};
      if (typeof headers !== "object") headers = {};

      // assignObject({ lang: unref(this.lang) }, params);

      const config = {
        headers,
        params,
        responseType,
        contentType,
      };

      const url = `${unref(this.baseUrl)}${endpointStr}`;
      let axiosRequest;

      const axiosMethod = methodStr.toLowerCase();
      try {
        switch (axiosMethod) {
          case "get":
          case "delete":
            if (typeof data === "object" && data !== null) {
              Object.assign(config.params, data);
            }
            axiosRequest = axios.get(url, config);
            break;
          case "post":
          case "update":
            axiosRequest = axios.post(url, data, config);
            break;
          case "put":
            axiosRequest = axios.put(url, data, config);
            break;

          default:
            reject(`undefined method ${axiosMethod}`);
            return;
        }

        axiosRequest
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            if (resolveCrash) {
              resolve(err);
            } else {
              if (!preventNotification) this.error(err as AxiosError);

              reject(err);
            }
          });
      } catch (e) {
        console.warn("-----REQUEST ERROR-----", e);
        if (resolveCrash) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          resolve(e);
        } else reject(e);
      }
    });
  }

  getDownloadLink(params: ISenderSendParams) {
    return new URL(
      `${unref(this.baseUrl)}${unref(params.endpoint)}?${new URLSearchParams(
        params.params as Record<string, any>
      )}`
    ).toString();
  }
}
